import { el } from './lib/dom.js';
import { DexGrid } from './views/DexGrid.js';
import { PokemonDetail } from './views/PokemonDetail.js';
import { SearchView } from './views/SearchView.js';
import { SettingsView } from './views/SettingsView.js';
import { MoveDetail } from './views/MoveDetail.js';
import { ItemDetail } from './views/ItemDetail.js';
import { AbilityDetail } from './views/AbilityDetail.js';
import { BerryDetail } from './views/BerryDetail.js';
import { RegionsView, RegionView, LocationView } from './views/LocationsView.js';
import { TeamView } from './views/TeamView.js';
import { NaturesView } from './views/NaturesView.js';
import { TypeChartView } from './views/TypeChartView.js';
import { CompareView } from './views/CompareView.js';
import { ProgressView } from './views/ProgressView.js';
import { BreedingView } from './views/BreedingView.js';
import { ShinyView } from './views/ShinyView.js';
import { WhosThatView } from './views/WhosThatView.js';
import { PinballView } from './views/PinballView.js';
import { PinballEditorView } from './views/PinballEditorView.js';
import { detailSkeleton, lookupSkeleton } from './components/skeletons.js';
import { prefersReducedMotion } from './store/settings.js';

const routes = [
  { pattern: /^#?\/?$/, view: () => DexGrid() },
  {
    pattern: /^#\/pokemon\/(\d+)$/,
    view: (m) => PokemonDetail(Number(m[1])),
    skeleton: detailSkeleton,
  },
  { pattern: /^#\/move\/(\d+)$/, view: (m) => MoveDetail(Number(m[1])), skeleton: lookupSkeleton },
  { pattern: /^#\/item\/(\d+)$/, view: (m) => ItemDetail(Number(m[1])), skeleton: lookupSkeleton },
  {
    pattern: /^#\/ability\/(\d+)$/,
    view: (m) => AbilityDetail(Number(m[1])),
    skeleton: lookupSkeleton,
  },
  {
    pattern: /^#\/berry\/(\d+)$/,
    view: (m) => BerryDetail(Number(m[1])),
    skeleton: lookupSkeleton,
  },
  { pattern: /^#\/locations$/, view: () => RegionsView() },
  { pattern: /^#\/region\/([\w-]+)$/, view: (m) => RegionView(m[1]) },
  { pattern: /^#\/location\/([\w-]+)$/, view: (m) => LocationView(m[1]) },
  { pattern: /^#\/team$/, view: () => TeamView() },
  { pattern: /^#\/natures$/, view: () => NaturesView() },
  { pattern: /^#\/types$/, view: () => TypeChartView() },
  { pattern: /^#\/compare$/, view: () => CompareView() },
  { pattern: /^#\/progress$/, view: () => ProgressView() },
  { pattern: /^#\/breeding$/, view: () => BreedingView() },
  { pattern: /^#\/shiny$/, view: () => ShinyView() },
  { pattern: /^#\/search$/, view: () => SearchView() },
  { pattern: /^#\/settings$/, view: () => SettingsView() },
  { pattern: /^#\/whos-that$/, view: () => WhosThatView() }, // hidden — Konami / logo taps
  { pattern: /^#\/pinball$/, view: () => PinballView() }, // hidden — linked from #/whos-that
  { pattern: /^#\/pinball-editor$/, view: () => PinballEditorView() }, // TEMPORARY dev tool — see PinballEditorView.js
];

function resolve(hash) {
  for (const route of routes) {
    const match = hash.match(route.pattern);
    if (match) return { route, match };
  }
  return { route: routes[0], match: [] };
}

const canAnimate = () =>
  typeof document.startViewTransition === 'function' && !prefersReducedMotion();

// The bottom-nav's own tabs — switching between these is a sibling swap,
// not drilling in/out, so it gets a plain cross-fade rather than a slide.
// Keep in step with components/Nav.js's ITEMS list.
const isTabRoute = (hash) =>
  hash === '#/' || hash === '' || hash === '#' || /^#\/(team|search|settings)(\/|$)/.test(hash);

// True back/forward (the browser's own back button, a swipe-back gesture,
// or history.back()) reports navigationType 'traverse' via the Navigation
// API; a tapped link or `location.hash =` assignment reports 'push'. (An
// earlier version of this listened for `popstate` instead — plain hash
// assignments fire that too in some Chrome/automation contexts, which made
// it useless as a back/forward signal. The Navigation API's own event is
// unambiguous and has the same browser support as startViewTransition.)
let wentBack = false;
if (typeof navigation !== 'undefined') {
  navigation.addEventListener('navigate', (e) => {
    if (e.navigationType === 'traverse') wentBack = true;
  });
}

let prevHash = location.hash || '#/';

/** push | pop | tab — which root view-transition animation to play. */
function navKind(hash) {
  const back = wentBack;
  wentBack = false;
  const prevWasTab = isTabRoute(prevHash);
  const nextIsTab = isTabRoute(hash);
  prevHash = hash;
  if (prevWasTab && nextIsTab) return 'tab';
  if (back) return 'pop';
  // Leaving a detail/sub-page for a tab reads as "going back up" even when
  // it's technically a fresh forward navigation (e.g. a "‹ Back" link).
  if (!prevWasTab && nextIsTab) return 'pop';
  return 'push';
}

let rerenderCurrent = null;

/** Re-run the active route (e.g. after the selected game changed). */
export function rerender() {
  if (rerenderCurrent) rerenderCurrent();
}

export function initRouter(host) {
  let current = null;

  function mount(node, animate, kind) {
    if (current) current.dispatchEvent(new CustomEvent('view:teardown'));
    current = node;
    if (animate && canAnimate()) {
      document.documentElement.dataset.nav = kind;
      const t = document.startViewTransition(() => host.replaceChildren(node));
      // A fast follow-up navigation skips the in-flight transition; that's
      // expected — swallow the AbortError from every promise it exposes.
      t?.ready?.catch(() => {});
      t?.finished?.catch(() => {});
      t?.updateCallbackDone?.catch(() => {});
    } else {
      host.replaceChildren(node);
    }
  }

  async function render() {
    const hash = location.hash || '#/';
    const { route, match } = resolve(hash);
    const kind = navKind(hash);

    // Instant feedback: a shaped skeleton for detail pages, a light
    // placeholder otherwise.
    const isPokemon = /^#\/pokemon\/\d+$/.test(hash);
    // TEMPORARILY DISABLED: Pokémon detail pages normally animate in (a
    // shared-element morph — the tapped card's sprite grows into the hero,
    // view-transition-name: pkmn-sprite in PokemonDetail.js). That's been
    // producing corrupted/scrambled pixels on the hero art for a moment
    // after navigating in, most likely the View Transitions API snapshotting
    // the <img> before it's done decoding. Turned off here (both mounts
    // instant, no transition at all) to isolate whether the animation is
    // really the cause. Re-enable by changing the two `false`/`!isPokemon`
    // below back to what every other route does (`isPokemon` / `true`).
    mount(
      route.skeleton
        ? route.skeleton(match)
        : el('div', { class: 'view view--loading' }, 'Loading…'),
      false,
      kind,
    );
    window.scrollTo(0, 0);

    try {
      const node = await route.view(match);
      if ((location.hash || '#/') !== hash) return; // superseded
      // Skeleton/placeholder -> content morphs via the View Transitions API.
      mount(node, !isPokemon, kind);
    } catch (err) {
      console.error(err);
      mount(
        el(
          'div',
          { class: 'view view--error' },
          el('h1', {}, 'Something went wrong'),
          el('p', {}, err.message || String(err)),
          el('a', { href: '#/', class: 'button' }, 'Back to the dex'),
        ),
        false,
      );
    }
  }

  rerenderCurrent = render;
  window.addEventListener('hashchange', render);
  render();
}
